/* eslint-disable react/forbid-elements, react/jsx-max-depth -- TODO: extract login form into feature component */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, LoginRequestSchema } from '@ooh/contracts';
import type { LoginRequest } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/login')({
  component: LoginRoute,
});

function LoginRoute() {
  const { auth } = Route.useRouteContext();
  return <LoginPage login={auth.login} />;
}

// ---------------------------------------------------------------------------
// Pure presentational + logic component (exported for testing)
// ---------------------------------------------------------------------------

export interface LoginPageProps {
  login: (email: string, password: string) => Promise<void>;
}

type LoginError = 'invalid-credentials' | 'rate-limited' | 'unknown';

export function LoginPage({ login }: LoginPageProps) {
  const { t } = useTranslation('login');
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<LoginError | null>(null);

  function errorMessage(kind: LoginError): string {
    switch (kind) {
      case 'invalid-credentials':
        return t('errors.invalidCredentials');
      case 'rate-limited':
        return t('errors.rateLimited');
      case 'unknown':
        return t('errors.unknown');
    }
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
  });

  async function onSubmit(data: LoginRequest) {
    setLoginError(null);
    try {
      await login(data.email, data.password);
      // TODO(media-units): route not yet in route tree — remove cast when route file exists
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await navigate({ to: '/media-units' as any });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setLoginError('invalid-credentials');
        } else if (err.status === 429) {
          setLoginError('rate-limited');
        } else {
          setLoginError('unknown');
        }
      } else {
        setLoginError('unknown');
      }
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            {/* Email field */}
            <div className="space-y-1">
              <label
                htmlFor="email"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('form.email')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={errors.email ? 'true' : 'false'}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                {...register('email')}
              />
              {errors.email ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              ) : null}
            </div>

            {/* Password field */}
            <div className="space-y-1">
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('form.password')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password ? 'true' : 'false'}
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                {...register('password')}
              />
              {errors.password ? (
                <p role="alert" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              ) : null}
            </div>

            {/* API-level error */}
            {loginError ? (
              <p role="alert" className="text-sm text-destructive">
                {errorMessage(loginError)}
              </p>
            ) : null}

            {/* Submit */}
            <Button type="submit" fullWidth disabled={isSubmitting}>
              {isSubmitting ? t('form.submitting') : t('form.submitIdle')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
